import { useCallback, useEffect, useMemo, useState } from "react";

const applyGroupOrder = (dedupeGroupNames, values, preferredOrder = []) => {
  const uniqueGroups = dedupeGroupNames(values);
  const preferredIndex = new Map(
    dedupeGroupNames(preferredOrder).map((group, index) => [group.toLowerCase(), index])
  );

  return uniqueGroups.sort((left, right) => {
    const leftOrder = preferredIndex.get(left.toLowerCase());
    const rightOrder = preferredIndex.get(right.toLowerCase());
    const leftHasOrder = leftOrder != null;
    const rightHasOrder = rightOrder != null;

    if (leftHasOrder && rightHasOrder) return leftOrder - rightOrder;
    if (leftHasOrder) return -1;
    if (rightHasOrder) return 1;

    return left.localeCompare(right, "de", { numeric: true, sensitivity: "base" });
  });
};

export default function useGroupOrder({
  dedupeGroupNames,
  readJsonStorage,
  writeJsonStorage,
  userVorlagenGroupOrderStorageKey,
  userBefundGroupOrderStorageKey,
  userProtokollGroupOrderStorageKey,
  defaultVorlagenGroups,
  defaultBefundGroups,
  defaultProtokollGroups,
  customVorlagenGroups,
  customBefundGroups,
  customProtokollGroups,
  vorlagenMods,
  befundGroupNames,
  protokollGroupNames,
}) {
  const [vorlagenGroupOrder, setVorlagenGroupOrder] = useState([]);
  const [befundGroupOrder, setBefundGroupOrder] = useState([]);
  const [protokollGroupOrder, setProtokollGroupOrder] = useState([]);

  useEffect(() => {
    setVorlagenGroupOrder(dedupeGroupNames(readJsonStorage(userVorlagenGroupOrderStorageKey, [])));
  }, [dedupeGroupNames, readJsonStorage, userVorlagenGroupOrderStorageKey]);

  useEffect(() => {
    setBefundGroupOrder(dedupeGroupNames(readJsonStorage(userBefundGroupOrderStorageKey, [])));
  }, [dedupeGroupNames, readJsonStorage, userBefundGroupOrderStorageKey]);

  useEffect(() => {
    setProtokollGroupOrder(dedupeGroupNames(readJsonStorage(userProtokollGroupOrderStorageKey, [])));
  }, [dedupeGroupNames, readJsonStorage, userProtokollGroupOrderStorageKey]);

  const persistVorlagenGroupOrder = useCallback((nextOrder) => {
    writeJsonStorage(userVorlagenGroupOrderStorageKey, dedupeGroupNames(nextOrder));
  }, [dedupeGroupNames, userVorlagenGroupOrderStorageKey, writeJsonStorage]);

  const persistBefundGroupOrder = useCallback((nextOrder) => {
    writeJsonStorage(userBefundGroupOrderStorageKey, dedupeGroupNames(nextOrder));
  }, [dedupeGroupNames, userBefundGroupOrderStorageKey, writeJsonStorage]);

  const persistProtokollGroupOrder = useCallback((nextOrder) => {
    writeJsonStorage(userProtokollGroupOrderStorageKey, dedupeGroupNames(nextOrder));
  }, [dedupeGroupNames, userProtokollGroupOrderStorageKey, writeJsonStorage]);

  const updateVorlagenGroupOrder = useCallback((updater) => {
    setVorlagenGroupOrder((prev) => {
      const rawNext = typeof updater === "function" ? updater(prev) : updater;
      const nextOrder = dedupeGroupNames(rawNext || []);
      persistVorlagenGroupOrder(nextOrder);
      return nextOrder;
    });
  }, [dedupeGroupNames, persistVorlagenGroupOrder]);

  const updateBefundGroupOrder = useCallback((updater) => {
    setBefundGroupOrder((prev) => {
      const rawNext = typeof updater === "function" ? updater(prev) : updater;
      const nextOrder = dedupeGroupNames(rawNext || []);
      persistBefundGroupOrder(nextOrder);
      return nextOrder;
    });
  }, [dedupeGroupNames, persistBefundGroupOrder]);

  const updateProtokollGroupOrder = useCallback((updater) => {
    setProtokollGroupOrder((prev) => {
      const rawNext = typeof updater === "function" ? updater(prev) : updater;
      const nextOrder = dedupeGroupNames(rawNext || []);
      persistProtokollGroupOrder(nextOrder);
      return nextOrder;
    });
  }, [dedupeGroupNames, persistProtokollGroupOrder]);

  const availableVorlagenGroups = useMemo(() => applyGroupOrder(
    dedupeGroupNames,
    [
      ...defaultVorlagenGroups,
      ...customVorlagenGroups,
      ...vorlagenMods,
    ],
    vorlagenGroupOrder.length ? vorlagenGroupOrder : defaultVorlagenGroups
  ), [
    customVorlagenGroups,
    dedupeGroupNames,
    defaultVorlagenGroups,
    vorlagenGroupOrder,
    vorlagenMods,
  ]);

  const availableBefundGroups = useMemo(() => applyGroupOrder(
    dedupeGroupNames,
    [
      ...defaultBefundGroups,
      ...customBefundGroups,
      ...befundGroupNames,
    ],
    befundGroupOrder.length ? befundGroupOrder : defaultBefundGroups
  ), [
    befundGroupNames,
    customBefundGroups,
    dedupeGroupNames,
    defaultBefundGroups,
    befundGroupOrder,
  ]);

  const availableProtokollGroups = useMemo(() => applyGroupOrder(
    dedupeGroupNames,
    [
      ...defaultProtokollGroups,
      ...customProtokollGroups,
      ...protokollGroupNames,
    ],
    protokollGroupOrder.length ? protokollGroupOrder : defaultProtokollGroups
  ), [
    customProtokollGroups,
    dedupeGroupNames,
    defaultProtokollGroups,
    protokollGroupNames,
    protokollGroupOrder,
  ]);

  return {
    availableVorlagenGroups,
    availableBefundGroups,
    availableProtokollGroups,
    updateVorlagenGroupOrder,
    updateBefundGroupOrder,
    updateProtokollGroupOrder,
  };
}
